import { useEffect, useState } from "react";
import { ShieldCheck, MessageCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";

export const PhoneVerify = ({ phone, token, onToken }) => {
  const [cfg, setCfg] = useState(null);
  const [pending, setPending] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [verified, setVerified] = useState(false);
  useEffect(() => { api.get("/verify/phone/config").then((r) => setCfg(r.data)).catch(() => setCfg({ enabled: false })); }, []);
  useEffect(() => { setPending(null); setVerified(false); setCode(""); }, [phone]);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/verify/phone/start", { phone });
      setPending(data);
      if (data.sent) toast.success(`Code sent to ${data.to}`);
      else if (data.token) { onToken(data.token, false); toast.message(data.message); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const check = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/verify/phone/check", { phone, code });
      setVerified(true); onToken(data.token, true); toast.success("Phone number verified");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  if (!cfg) return null;
  const digits = (phone || "").replace(/\D/g, "");
  if (digits.length < 10) return <p className="text-xs text-[#0a0d12]/55" data-testid="phone-verify-hint">Enter your mobile number, then verify it with a one-time code.</p>;
  if (verified || (token && pending?.sent === false)) {
    return (
      <div className="flex items-center gap-2 text-sm text-green-800 bg-green-50 border border-green-200 rounded-md px-3 py-2" data-testid="phone-verify">
        <ShieldCheck size={16} /> {verified ? "Phone number verified." : "Verification unavailable right now — you can continue; our team will confirm by phone."}
      </div>
    );
  }
  return (
    <div className="rounded-md border border-[#d6aa43]/40 bg-[#f8f4e8] p-3" data-testid="phone-verify">
      <div className="flex flex-wrap items-center gap-2">
        <MessageCircle size={16} className="text-[#7a5a0f]" />
        <span className="text-sm text-[#0a0d12]/80 flex-1">{pending?.sent ? `We sent a 6-digit code to ${pending.to} by WhatsApp/SMS.` : "Verify your number — we will send a 6-digit code by WhatsApp or SMS."}</span>
        <button type="button" onClick={send} disabled={busy} className="px-3 py-1.5 rounded-md bg-[#155e3f] text-[#d6aa43] text-xs font-bold disabled:opacity-50" data-testid="phone-verify-send">
          {busy && !pending ? <Loader2 size={14} className="animate-spin" /> : pending?.sent ? "Resend code" : "Send code"}
        </button>
      </div>
      {pending?.sent && (
        <div className="flex items-center gap-2 mt-3">
          <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" maxLength={6} placeholder="6-digit code" className="px-3 py-2 border border-black/15 rounded-md text-sm w-40 tracking-widest" data-testid="phone-verify-code" />
          <button type="button" onClick={check} disabled={busy || code.length < 6} className="dk-btn-primary !py-2 !px-4 text-xs" data-testid="phone-verify-check">Verify</button>
        </div>
      )}
      {pending && pending.sent === false && <p className="text-xs text-[#0a0d12]/60 mt-2">{pending.message}</p>}
    </div>
  );
};
